/**
 * -------------------------------------------------------------
 * File: featureUtils.ts
 * Description:
 *  - Combines features from multiple package products in API response
 *  - Removes duplicate features based on `feature_key`
 *  - Filters out disabled / invalid features
 *
 * Rules:
 *  - Ignore boolean features when value is "false" or "-1"
 *  - Ignore limit features when value is "0"
 *
 * Author: Harvinder Singh
 * Created On: 09-Feb-2026
 * -------------------------------------------------------------
 */

import { useGlobalTrialState } from "../composable/freeTrail/useGlobalTrialState"

export interface FeatureConfig {
    feature_key: string
    display_name: string
    value_type: 'boolean' | 'limit' | string
    value: string | number
  }
  
  /**
   * Your existing package feature model
   */
  export interface PackageFeature {
    product_name: string
    renewal_date: string
    start_date: string
    subscription_type: string
    features: FeatureConfig[] | null
  }
  
  /**
   * Generic API response where:
   * key   → product code (core, mmc, etc.)
   * value → PackageFeature
   */
  export type ApiResponse<T extends Record<string, PackageFeature>> = {
    data: T
  }
  
  /**
   * Combines and normalizes features from API response
   *
   * @param response - API response containing multiple package features
   * @returns FeatureConfig[] - Unique and valid feature list
   */
  export function getCombinedFeatures<
    T extends Record<string, PackageFeature>
  >(response: ApiResponse<T>, isCustomerPreferredUid: boolean): FeatureConfig[] {
    const featureMap = new Map<string, FeatureConfig>()
    // Iterate through all product packages
    Object.values(response.data).forEach(pkg => {
      if (!Array.isArray(pkg.features)) return
  
      pkg.features.forEach(feature => {
        const { feature_key, value_type } = feature
        const valueStr = String(feature.value)
  
        /**
         * Ignore boolean features when disabled
         */
        if (
          value_type === 'boolean' &&
          (valueStr === 'false' || valueStr === '-1')
        ) {
          return
        }
  
        /**
         * Ignore limit features with zero value
         */
        if (
          value_type === 'limit' &&
          valueStr === '0'
        ) {
          return
        }

        /**
         * Ignore lead_ads feature for customer preferred uid accounts
         */

        if(isCustomerPreferredUid && feature_key === 'lead_ads') {
          return
        }
  
        /**
         * Deduplicate by feature_key
         * First occurrence wins
         */
        if (!featureMap.has(feature_key)) {
          featureMap.set(feature_key, feature)
        }
      })
    })
  
    return Array.from(featureMap.values())
  }
  
  export function checkFreetrailAccount(trialData: any, userStore: any, navigateToBilling: () => void) {
    /**
     * Initialize globally ONLY ONCE
     */
    useGlobalTrialState(
      trialData,
      userStore,
      {
        navigateToBilling,

        setIsTrailAccount: (value) => {
          userStore.setIsTrailAccount(value);
        },

        setIsTrialExpired: (value) => {
          userStore.setIsTrialExpired(value);
        },
      }
    );
  }